import React, { type JSX } from "react";
import {
  HandThumbUpIcon,
  HandThumbDownIcon,
} from "@heroicons/react/24/outline";
import {
  HandThumbUpIcon as HandThumbUpIconSolid,
  HandThumbDownIcon as HandThumbDownIconSolid,
} from "@heroicons/react/24/solid";

const RECONNECTION_CLASSES: Record<string, string> = {
  "reconnection-error": "text-red-600 bg-red-50 border-red-200",
  "reconnection-success": "text-green-700 bg-green-50 border-green-200",
};

/** Inline status bar for connection loss / recovery notices. */
export function ReconnectionNotice({ message }: { message: any }): JSX.Element {
  const colorClass =
    RECONNECTION_CLASSES[message.type as string] || "text-gray-500 bg-gray-50 border-gray-200";

  return (
    <div className="flex justify-center my-2">
      <div className={`text-xs px-3 py-1.5 rounded-full border ${colorClass}`}>{message.text}</div>
    </div>
  );
}

/** A user's own message, bubbled on the right. */
export function UserMessage({ text }: { text?: string }): JSX.Element {
  return (
    <div className="flex w-full max-w-[85%] sm:max-w-xs lg:max-w-md justify-end">
      <div className="px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-cvc-grey text-cvc-text">
        <p className="text-sm sm:text-base leading-relaxed whitespace-pre-line break-words">
          {text}
        </p>
      </div>
    </div>
  );
}

/** Centred product introduction, shown above the conversation starters. */
export function WelcomeHeader(): JSX.Element {
  return (
    <div className="w-full text-center py-3 sm:py-8">
      <h1
        className="text-lg leading-6 sm:text-[40px] sm:leading-[40px] tablet:text-[30px] tablet:leading-[32px] font-medium mb-4 font-cvcDisplay"
        style={{ color: "#234E81" }}
      >
        Welcome to DealOS,
        <br />
        CVC&apos;s proprietary AI platform.
      </h1>
      <p className="text-base leading-6 text-cvc-text max-w-2xl mx-auto px-4 font-normal font-cvcBody">
        Try our beta tasks below, with more powerful features on the way.
      </p>
    </div>
  );
}

/** One thumb button, solid once it is the chosen rating. */
function ThumbButton({
  chosen,
  scaling,
  title,
  onClick,
  SolidIcon,
  OutlineIcon,
  solidClass,
  outlineClass,
}: {
  chosen: boolean;
  scaling: boolean;
  title: string;
  onClick: () => void;
  SolidIcon: typeof HandThumbUpIconSolid;
  OutlineIcon: typeof HandThumbUpIcon;
  solidClass: string;
  outlineClass: string;
}): JSX.Element {
  let scaleClass = "hover:bg-gray-100 hover:scale-105";
  if (chosen) {
    scaleClass = scaling ? "scale-125" : "hover:scale-105";
  }

  return (
    <button
      onClick={onClick}
      className={`p-1 rounded-full transition-all duration-200 transform ${scaleClass}`}
      title={title}
      aria-label={title}
    >
      {chosen ? (
        <SolidIcon className={`h-4 w-4 ${solidClass} transition-colors duration-200`} />
      ) : (
        <OutlineIcon className={`h-4 w-4 ${outlineClass} transition-colors duration-200`} />
      )}
    </button>
  );
}

/** Thumbs up / down controls, shown only on the latest bot message. */
export function FeedbackControls({
  index,
  chosen,
  scaling,
  onFeedback,
}: {
  index: number;
  chosen?: "thumbs_up" | "thumbs_down";
  scaling: boolean;
  onFeedback: (index: number, feedbackType: "thumbs_up" | "thumbs_down") => void;
}): JSX.Element {
  return (
    <div className="mt-2 flex items-center gap-2">
      <span className="text-xs text-cvc-text/70">Help me get better:</span>
      <ThumbButton
        chosen={chosen === "thumbs_up"}
        scaling={scaling}
        title="Good response"
        onClick={() => onFeedback(index, "thumbs_up")}
        SolidIcon={HandThumbUpIconSolid}
        OutlineIcon={HandThumbUpIcon}
        solidClass="text-cvc-green"
        outlineClass="text-cvc-text/50 hover:text-cvc-green"
      />
      <ThumbButton
        chosen={chosen === "thumbs_down"}
        scaling={scaling}
        title="Bad response"
        onClick={() => onFeedback(index, "thumbs_down")}
        SolidIcon={HandThumbDownIconSolid}
        OutlineIcon={HandThumbDownIcon}
        solidClass="text-cvc-coral"
        outlineClass="text-cvc-text/50 hover:text-cvc-coral"
      />
    </div>
  );
}
